/* eslint-disable @tanstack/query/exhaustive-deps */
import { useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { shareVisualsProcessing as isProcessing } from '../../shared';

import type { PetitionShareVisualsReturn } from './api';
import { getPetitionShareVisuals } from './api';

type Petition = Pick<StarterDashboardV2PageData['petition'], 'slug' | 'shareVisuals'>;

const QUERY_KEY = 'share-visuals';
const CACHE_TIME_MS = 5 * 60 * 1000;
const REFETCH_INTERVAL_MS = 1000;
const REFETCH_INTERVAL_INCREMENT_MS = 500;
const MAX_REFETCH_COUNT = 10;

function isLoaded(shareVisuals: PetitionShareVisualsReturn) {
	return shareVisuals.length > 0 && !isProcessing(shareVisuals);
}

export function useShareVisuals({ petition }: { petition: Petition }) {
	const utilityContext = useUtilityContext();

	// Count the number of times we have attempted to fetch the data.  Once we reach
	// the maximum refetches we disable the query and show an appropriate message to the user.
	const fetchCount = useRef(0);
	const [isTimeout, setIsTimeout] = useState(false);

	// If the modal has not been opened, we will seed the `cache` with the initial `pageData` share visuals.
	// Once the modal has been opened (i.e. any subsequent re-opens), we will switch to using the cache,
	// since the `pageData` never gets refreshed with the values fetched by `useQuery`.
	// If the cache expires and the `pageData` hasn't been refreshed , the user will briefly see the loading spinner.
	const cachedShareVisuals = useQueryClient().getQueryData<PetitionShareVisualsReturn>([QUERY_KEY, petition.slug]);
	const initialShareVisuals = cachedShareVisuals ?? petition.shareVisuals;

	const loaded = isLoaded(initialShareVisuals);
	const queryEnabled = !loaded && !isTimeout;

	const responseState = useQuery<PetitionShareVisualsReturn>({
		queryKey: [QUERY_KEY, petition.slug],
		queryFn: async () => {
			fetchCount.current += 1;
			setIsTimeout(fetchCount.current >= MAX_REFETCH_COUNT);
			return getPetitionShareVisuals(utilityContext, petition.slug);
		},
		enabled: queryEnabled,
		refetchInterval: (query) => {
			// Stop refetching if we've reached the maximum refetch count
			if (fetchCount.current >= MAX_REFETCH_COUNT) return false;

			// Check the actual current data from the query
			const currentData = query.state.data ?? initialShareVisuals;
			const dataProcessing = isProcessing(currentData);
			const dataLoaded = currentData.length > 0 && !dataProcessing;

			if (dataLoaded) return false;

			// Calculate incremental backoff, add .5s to the previous interval: 1s, 1.5s, 2s, 2.5s, etc.
			return REFETCH_INTERVAL_MS + fetchCount.current * REFETCH_INTERVAL_INCREMENT_MS;
		},
		initialData: initialShareVisuals,
		staleTime: loaded ? CACHE_TIME_MS : 0, // Increase stale time once loaded
		gcTime: CACHE_TIME_MS,
	});

	return {
		...responseState,
		isTimeout,
	};
}
